import Dialog from "@mui/material/Dialog";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  selectUndispatchedCargos,
  selectUndispatchedCargosRequestState,
} from "../../../ducks/data/undispatched-cargos/selectors";
import { useAppDispatch } from "../../../redux-store";
import {
  loadUndispatchedCargos,
  moveUndispatchedCargoToRoute,
} from "../../../ducks/data/undispatched-cargos";
import { DriverUndispatchedCargo } from "../../../ducks/data/undispatched-cargos/types";
import { LoadingButton } from "@mui/lab";
import { loadRouteWithStops } from "../../../ducks/data/route-with-stops";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import "./AddCargosFromUndispatchedDialog.scss";
import { useL10n } from "../../../l10n";
import { DateTime } from "luxon";

export const AddCargosFromUndispatchedDialog = ({
  routeId,
  onClose,
}: {
  routeId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation(["translation", "undispatchedCargos"]);
  const dispatch = useAppDispatch();
  const undispatchedCargos = useSelector(selectUndispatchedCargos);
  const loadingState = useSelector(selectUndispatchedCargosRequestState);
  useEffect(() => {});

  useEffect(() => {
    dispatch(loadUndispatchedCargos());
  }, [dispatch]);

  return (
    <Dialog open onClose={onClose}>
      <div className={"px-4 py-2"}>
        <h3>{t("undispatchedCargos:title")}:</h3>
        <br />
        {loadingState === "loading" && (
          <div>{t("undispatchedCargos:loading")}</div>
        )}
        {loadingState === "error" && (
          <div>{t("undispatchedCargos:errorLoading")}</div>
        )}
        {loadingState === "success" && (
          <div className="flex flex-col gap-6">
            {undispatchedCargos.length === 0 ? (
              <div>{t("undispatchedCargos:noCargos")}</div>
            ) : (
              <>
                {undispatchedCargos.map((cargo) => (
                  <UndispatchedCargo
                    routeId={routeId}
                    cargo={cargo}
                    key={cargo.id}
                    onAddSuccess={() => onClose()}
                  />
                ))}
              </>
            )}
          </div>
        )}
        <div className="mt-4 flex justify-end">
          <Button variant="outlined" onClick={onClose}>
            {t("translation:Back")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

const UndispatchedCargo = ({
  cargo,
  routeId,
  onAddSuccess,
}: {
  cargo: DriverUndispatchedCargo;
  routeId: string;
  onAddSuccess: () => void;
}) => {
  const { t } = useTranslation(["translation", "undispatchedCargos"]);
  const [adding, setAdding] = useState(false);
  const dispatch = useAppDispatch();

  const onAddShipmentClick = async () => {
    setAdding(true);
    try {
      await dispatch(
        moveUndispatchedCargoToRoute({ cargoId: cargo.id, routeId })
      ).unwrap();
      onAddSuccess();
    } catch (error) {
      setAdding(false);
      return;
    }
    setTimeout(async () => {
      await dispatch(loadRouteWithStops(routeId)).unwrap();
    }, 250);
  };

  const l10n = useL10n();

  const formatDate = (date: DateTime | null) =>
    date === null
      ? ""
      : date.get("hour") !== 0 || date.get("minute") !== 0
        ? l10n.formatDateAndTime(date)
        : l10n.formatDate(date);

  return (
    <div className="add-cargos-from-undispatched-dialog">
      <table>
        <tbody>
          <tr>
            <td>{t("undispatchedCargos:from")}:</td>
            <td>{cargo.from.addressString}</td>
          </tr>
          <tr>
            <td>{t("undispatchedCargos:date")}:</td>
            <td>{formatDate(cargo.from.dateTime)}</td>
          </tr>
          <tr>
            <td>{t("undispatchedCargos:to")}:</td>
            <td>{cargo.to?.addressString}</td>
          </tr>
          <tr>
            <td>{t("undispatchedCargos:date")}:</td>
            <td>{formatDate(cargo.to.dateTime)}</td>
          </tr>
          {cargo.client && (
            <tr>
              <td>{t("translation:client")}</td>
              <td>{cargo.client}</td>
            </tr>
          )}
          {cargo.invoiceRef && (
            <tr>
              <td>{t("undispatchedCargos:invoiceRef")}</td>
              <td>{cargo.invoiceRef}</td>
            </tr>
          )}
        </tbody>
      </table>
      <LoadingButton
        onClick={onAddShipmentClick}
        loading={adding}
        variant="contained"
      >
        {t("add")}
      </LoadingButton>
    </div>
  );
};
